import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '@redhand/ui';
import { systemClock } from '@reos/config';
import { t } from '@reos/i18n';
import { useSession } from '../stores/session.js';
import { isOwnerOrAdmin } from '../lib/capabilities.js';
import {
  fetchActiveCases,
  officeEconomySummary,
  stalledCasesOverview,
  type OfficeEconomyItem,
} from '../lib/manager-overview.js';
import { listInvoices } from '../lib/invoices-api.js';
import { getAdvertisingCosts } from '../lib/advertising-api.js';
import { formatMoney } from '../lib/properties.js';
import { stageLabel } from '../lib/case-stages.js';
import { DomainApiError } from '../lib/api-error.js';
import { PageHead } from '../components/page-head.js';
import { ListTable } from '../components/list-table.js';

/**
 * `/ekonomika-kancelare` (krok AI9 bod 2) — manazerský přehled kanceláře.
 *
 * Do kroku AI9 byla tato cesta jen `preparing: true` placeholder v nav.ts.
 * Jádro: zakázky BEZ POHYBU 14 dní (`stalledCasesOverview`) — kde se
 * zakázky zasekávají, první otázka manažerského přehledu ze zadání bodu 2.
 * Výkon makléřů, cash-flow provizí a cena inzerce za reakci zatím chybí
 * (`## ZBÝVÁ` v reportu kroku AI9) — stránka je JÁDRO, ne celý brief.
 *
 * Owner/admin only (`isOwnerOrAdmin` — stejná brána jako `nastaveni.tsx` a
 * `isNavItemVisible`, žádná druhá definice role). Server řeší datovou
 * izolaci sám (`listCases` vrací owner/adminovi celý tenant, broker jen svůj
 * dosah — `packages/case/src/public/plugin.ts`); tahle brána je jen to, co
 * uvidí v UI běžný makléř, kdyby na adresu přišel přímo.
 */
/** Popisky ctyr cisel. Funkce, ne hodnoty: `t()` se musi volat pri renderu. */
const KPI_LABELS: Readonly<Record<OfficeEconomyItem['key'], () => string>> = {
  commissionThisMonth: () => t('webAdmin.ekonomikaKancelare.kpiCommissionThisMonth', { locale: 'cs' }),
  openCases: () => t('webAdmin.ekonomikaKancelare.kpiOpenCases', { locale: 'cs' }),
  overdueInvoices: () => t('webAdmin.ekonomikaKancelare.kpiOverdueInvoices', { locale: 'cs' }),
  adSpend: () => t('webAdmin.ekonomikaKancelare.kpiAdSpend', { locale: 'cs' }),
};

/**
 * Hodnota dlazdice. Penize jdou pres `formatMoney` (jediny formatovac castek
 * v adminu); pocet je cislo. Smisene meny `officeEconomySummary` do jednoho
 * souctu nedava (`currency === null`) — pak se ukaze cista castka bez meny,
 * ne vymyslena „Kc".
 */
function kpiValue(item: OfficeEconomyItem): string {
  if (item.count !== null) return String(item.count);
  if (item.total === null) return '—';
  if (item.currency === null) return item.total;
  return formatMoney({ amount: item.total, currency: item.currency }) ?? item.total;
}

export function EkonomikaKancelarePage(): React.JSX.Element {
  const user = useSession((s) => s.user);
  const allowed = isOwnerOrAdmin(user?.roles ?? []);
  const [now] = React.useState(() => systemClock.now());

  const cases = useQuery({
    queryKey: ['ekonomika-kancelare', 'stalled-cases'],
    enabled: allowed,
    queryFn: () => fetchActiveCases(),
  });

  /*
   * Krok UXK3c bod 3 — ctyri cisla nahore. Doklady a naklady na inzerci jsou
   * VLASTNI dotazy: kdyz nektery selze (nebo kancelar na finance nema pravo),
   * ztichne jen pasek dlazdic, tabulka zakazek bez pohybu zustava. Proto taky
   * `retry: false` a zadny `role="alert"` — dlazdice nejsou brana obrazovky.
   */
  const invoices = useQuery({
    queryKey: ['ekonomika-kancelare', 'invoices'],
    enabled: allowed,
    retry: false,
    queryFn: () => listInvoices({}),
  });
  const adCosts = useQuery({
    queryKey: ['ekonomika-kancelare', 'ad-costs'],
    enabled: allowed,
    retry: false,
    queryFn: () => getAdvertisingCosts({ groupBy: 'month' }),
  });

  if (!allowed) {
    return (
      <div className="p-6" data-testid="ekonomika-kancelare-page">
        <EmptyState
          title={t('webAdmin.ekonomikaKancelare.ownerOnlyTitle', { locale: 'cs' })}
          description={t('webAdmin.ekonomikaKancelare.ownerOnlyDescription', { locale: 'cs' })}
        />
      </div>
    );
  }

  const stalled = stalledCasesOverview(cases.data ?? [], now);
  const economy = officeEconomySummary(
    {
      cases: cases.data ?? [],
      invoices: invoices.data ?? [],
      adCosts: adCosts.data?.summary ?? [],
    },
    now,
  );

  return (
    <div className="p-6" data-testid="ekonomika-kancelare-page">
      <PageHead
        title={t('webAdmin.ekonomikaKancelare.title', { locale: 'cs' })}
        subtitle={t('webAdmin.ekonomikaKancelare.perex', { locale: 'cs' })}
      />

      {economy.length > 0 && (
        <div className="rh-a2-stats rh-a2-stats--4" data-testid="ekonomika-kpis">
          {economy.map((item) => (
            <div key={item.key} className="rh-a2-stat" data-testid={`ekonomika-kpi-${item.key}`}>
              <span className="rh-a2-stat__label">{KPI_LABELS[item.key]()}</span>
              <strong className="rh-a2-stat__value">{kpiValue(item)}</strong>
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-1 text-lg font-semibold">{t('webAdmin.ekonomikaKancelare.stalledTitle', { locale: 'cs' })}</h2>
      <p className="mb-4 max-w-2xl text-sm text-gray-600">
        {t('webAdmin.ekonomikaKancelare.stalledDescription', { locale: 'cs' })}
      </p>

      {cases.isLoading && <p role="status">{t('webAdmin.ekonomikaKancelare.loading', { locale: 'cs' })}</p>}
      {cases.isError && (
        <p role="alert" className="text-red-600">
          {cases.error instanceof DomainApiError
            ? cases.error.message
            : t('webAdmin.ekonomikaKancelare.loadError', { locale: 'cs' })}
        </p>
      )}

      {!cases.isLoading && !cases.isError && stalled.length === 0 && (
        <EmptyState
          title={t('webAdmin.ekonomikaKancelare.emptyTitle', { locale: 'cs' })}
          description={t('webAdmin.ekonomikaKancelare.emptyDescription', { locale: 'cs' })}
        />
      )}

      {/*
        Krok QAPRUCH1: seznam jede pres SDILENOU `ListTable`, ne pres vlastni
        `<table>`. Pruchod adminu 30. 10. 2026 nasel na 390 px sloupec „Fáze"
        o sirce 43 px — presne vada, kvuli ktere krok FUNK1 tuhle komponentu
        zavedl: pod 900 px z radku dela kartu (`admin-v2.css`,
        `.rh-a2-tbl td[data-label]`) a popisek karty bere z TEHOZ retezce
        jako hlavicku. Vlastni `<table class="w-full text-sm">" zadnou z tech
        dvou veci nemela.
      */}
      {stalled.length > 0 && (
        <ListTable
          testId="ekonomika-kancelare-stalled-table"
          caption={t('webAdmin.ekonomikaKancelare.stalledTitle', { locale: 'cs' })}
          rows={stalled}
          rowKey={(item) => item.id}
          columns={[
            {
              key: 'case',
              label: t('webAdmin.ekonomikaKancelare.colCase', { locale: 'cs' }),
              cell: (item) => (
                <Link to="/zakazky/$id" params={{ id: item.id }} className="text-blue-600 hover:underline">
                  {item.title.trim() === '' ? item.id : item.title}
                </Link>
              ),
            },
            {
              key: 'stage',
              label: t('webAdmin.ekonomikaKancelare.colStage', { locale: 'cs' }),
              cell: (item) => stageLabel(item.stage),
            },
            {
              key: 'days',
              label: t('webAdmin.ekonomikaKancelare.colDays', { locale: 'cs' }),
              cell: (item) =>
                t('webAdmin.ekonomikaKancelare.daysSince', {
                  locale: 'cs',
                  values: {
                    days: Math.floor(
                      (now.getTime() - new Date(item.updatedAt).getTime()) / (24 * 60 * 60 * 1000),
                    ),
                  },
                }),
            },
          ]}
        />
      )}
    </div>
  );
}
